import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import FilesService, { IUploadFileUrl } from '@services/FilesService';
import { env } from 'env';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import EventsService from '@services/EventsService';
import BrandingsService from '@services/BrandingsService';
import {
  convertVideoFileToWebm,
  parseAndValidateVideoFile,
} from './videoConverter';

const ALLOWED_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg'];
const ALLOWED_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];

const isAllowedImageFilename = (name: string) =>
  /\.(png|jpe?g)$/i.test(name.trim());

const isAllowedVideoFilename = (name: string) =>
  /\.(mp4|webm|mov)$/i.test(name.trim());

const normalizeSpotPreviewImage = (spot: any) => {
  if (!spot || typeof spot !== 'object') {
    return spot;
  }

  const data =
    spot.data && typeof spot.data === 'object' ? { ...spot.data } : {};
  const topLevelPreviewImage =
    typeof spot.previewImage === 'string' && spot.previewImage.length
      ? spot.previewImage
      : null;

  if (topLevelPreviewImage && !data.previewImage) {
    data.previewImage = topLevelPreviewImage;
  }

  const { previewImage, ...rest } = spot;
  return {
    ...rest,
    data,
  };
};

const normalizeUeIdentifier = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const findSpotByUeIdentifier = (
  spots: any[],
  ueIdentifier: string | null | undefined,
) => {
  const normalizedUeIdentifier = normalizeUeIdentifier(ueIdentifier);

  return spots.find(
    (spot: any) =>
      normalizeUeIdentifier(spot?.ueIdentifier) === normalizedUeIdentifier,
  );
};

type SpotDataPayload = {
  location: string;
  url: string;
  md5?: string;
  previewImage?: string;
  [key: string]: unknown;
};

const buildBrandingSpotMutationPayload = (
  dataPayload: SpotDataPayload,
  spotType: string,
  extraData: Record<string, unknown> = {},
) => {
  const { previewImage, md5, ...mediaData } = dataPayload;

  return {
    isActive: true,
    type: spotType,
    data: {
      ...mediaData,
      ...extraData,
    },
    previewImage: spotType === 'VIDEO' ? previewImage || '' : '',
    ...(md5 ? { md5 } : {}),
  };
};

const getSpotIdentity = (spot: any) =>
  spot?.brandingSpotId ||
  spot?.roomSpotId ||
  spot?.id ||
  normalizeUeIdentifier(spot?.ueIdentifier);

const mergeSpotIntoList = (spots: any[], expectedSpot: any) => {
  const expectedIdentity = getSpotIdentity(expectedSpot);
  const expectedUeIdentifier = normalizeUeIdentifier(
    expectedSpot?.ueIdentifier,
  );
  let didMerge = false;

  const merged = spots.map((spot) => {
    const isSameSpot =
      (!!expectedIdentity && getSpotIdentity(spot) === expectedIdentity) ||
      (!!expectedUeIdentifier &&
        normalizeUeIdentifier(spot?.ueIdentifier) === expectedUeIdentifier);

    if (!isSameSpot) {
      return spot;
    }

    didMerge = true;
    return {
      ...spot,
      ...expectedSpot,
      data: {
        ...(spot?.data ?? {}),
        ...(expectedSpot?.data ?? {}),
      },
    };
  });

  return didMerge ? merged : [expectedSpot, ...merged];
};

export type CustomSpotsEditorProps = {
  open: boolean;
  onClose: () => void;
  suppressAssetUpdatedModal?: boolean;
};

export const useCustomSpotsEditor = ({
  onClose,
  suppressAssetUpdatedModal = false,
}: Pick<CustomSpotsEditorProps, 'onClose' | 'suppressAssetUpdatedModal'>) => {
  const { t } = useTranslation();
  const {
    currentRoom,
    roomSpots,
    setRoomSpots,
    brandingSpots,
    setBrandingSpots,
    selectedCustomizationUeId,
    setSelectedCustomizationUeId,
    selectedCustomizationTargetSize,
    setSelectedCustomizationTargetSize,
    selectedCustomizationSpotType,
    setSelectedCustomizationSpotType,
    setShowCustomSpotsEditor,
    isBrandingModeEnabled,
    currentBrandingId,
    currentParticipant,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoFileInputRef = useRef<HTMLInputElement | null>(null);
  const previewImageFileInputRef = useRef<HTMLInputElement | null>(null);
  const previousSpotSnapshotRef = useRef<{
    existed: boolean;
    spotId: string | null;
    dataPayload: { location?: string; url?: string; md5?: string } | null;
    previousUrl: string | null;
  } | null>(null);
  const lastUpdatedSpotIdRef = useRef<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isPreviewImageDragging, setIsPreviewImageDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStepMessage, setUploadStepMessage] = useState<string | null>(
    null,
  );
  const [isApplyingToSurface, setIsApplyingToSurface] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setUploadedAsset] = useState<IUploadFileUrl | null>(null);
  const [isFailureOpen, setIsFailureOpen] = useState(false);
  const [failureDetailsOpen, setFailureDetailsOpen] = useState(false);
  const [failureMessage, setFailureMessage] = useState<string>('');
  const [isAssetUpdatedOpen, setIsAssetUpdatedOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [, setSelectedImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [selectedVideoFile, setSelectedVideoFile] = useState<File | null>(null);
  const [selectedVideoLocalUrl, setSelectedVideoLocalUrl] = useState<
    string | null
  >(null);
  const [videoPreviewImageFile, setVideoPreviewImageFile] =
    useState<File | null>(null);
  const [videoPreviewLocalUrl, setVideoPreviewLocalUrl] = useState<
    string | null
  >(null);

  const isVideoSpot = selectedCustomizationSpotType === 'VIDEO';
  const targetWidth = selectedCustomizationTargetSize?.width || null;
  const targetHeight = selectedCustomizationTargetSize?.height || null;
  const targetAspect =
    targetWidth && targetHeight ? targetWidth / targetHeight : 16 / 9;

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      if (videoPreviewLocalUrl) {
        URL.revokeObjectURL(videoPreviewLocalUrl);
      }
      if (selectedVideoLocalUrl) {
        URL.revokeObjectURL(selectedVideoLocalUrl);
      }
    };
  }, [previewUrl, videoPreviewLocalUrl, selectedVideoLocalUrl]);

  const unsupportedImageFormatText = useMemo(
    () =>
      t('customSpotsEditor.failureUnsupportedFormat', {
        defaultValue:
          'Unsupported format. Please upload a PNG or JPG file and try again.',
      }),
    [t],
  );

  const unsupportedVideoFormatText = useMemo(
    () =>
      t('customSpotsEditor.failureUnsupportedVideoFormat', {
        defaultValue:
          'Unsupported video format. Please upload a WebM video, or an MP4/MOV file to convert.',
      }),
    [t],
  );

  const requirementsText = useMemo(() => {
    if (isVideoSpot) {
      return '';
    }

    const reqParts: string[] = [
      t('customSpotsEditor.requirements.format', {
        defaultValue: 'Supported formats: PNG, JPG, JPEG',
      }),
    ];

    if (targetWidth && targetHeight) {
      reqParts.push(
        t('customSpotsEditor.requirements.size', {
          defaultValue: 'Size: {{w}}×{{h}}',
          w: targetWidth,
          h: targetHeight,
        }),
      );
    }

    return reqParts.join('   ');
  }, [t, targetHeight, targetWidth, isVideoSpot]);

  const spotsSource = useMemo(
    () =>
      currentBrandingId && isBrandingModeEnabled ? brandingSpots : roomSpots,
    [currentBrandingId, isBrandingModeEnabled, brandingSpots, roomSpots],
  );

  const existingSpotImageUrl = useMemo(() => {
    if (!selectedCustomizationUeId) {
      return null;
    }

    const spotMatch = findSpotByUeIdentifier(
      spotsSource,
      selectedCustomizationUeId,
    );
    const url = spotMatch?.data?.url || spotMatch?.data?.location || null;

    if (typeof url !== 'string' || !url.length) {
      return null;
    }

    return url;
  }, [spotsSource, selectedCustomizationUeId]);

  const resetState = useCallback(() => {
    setIsDragging(false);
    setIsPreviewImageDragging(false);
    setIsUploading(false);
    setUploadProgress(0);
    setUploadStepMessage(null);
    setIsApplyingToSurface(false);
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    setSelectedImageSize(null);
    setIsFailureOpen(false);
    setFailureDetailsOpen(false);
    setFailureMessage('');
    setIsAssetUpdatedOpen(false);
    setIsReverting(false);
    previousSpotSnapshotRef.current = null;
    lastUpdatedSpotIdRef.current = null;

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setSelectedVideoFile(null);
    if (selectedVideoLocalUrl) {
      URL.revokeObjectURL(selectedVideoLocalUrl);
    }
    setSelectedVideoLocalUrl(null);
    setVideoPreviewImageFile(null);

    if (videoPreviewLocalUrl) {
      URL.revokeObjectURL(videoPreviewLocalUrl);
    }
    setVideoPreviewLocalUrl(null);
  }, [previewUrl, selectedVideoLocalUrl, videoPreviewLocalUrl]);

  const clearSelectedImageDraft = useCallback(() => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(null);
    setSelectedImageSize(null);
    setPreviewUrl(null);
  }, [previewUrl]);

  const clearSelectedVideoDraft = useCallback(() => {
    if (selectedVideoLocalUrl) {
      URL.revokeObjectURL(selectedVideoLocalUrl);
    }
    if (videoPreviewLocalUrl) {
      URL.revokeObjectURL(videoPreviewLocalUrl);
    }

    setSelectedVideoFile(null);
    setSelectedVideoLocalUrl(null);
    setVideoPreviewImageFile(null);
    setVideoPreviewLocalUrl(null);
  }, [selectedVideoLocalUrl, videoPreviewLocalUrl]);

  const openFailure = useCallback((message: string) => {
    setFailureMessage(message);
    setFailureDetailsOpen(false);
    setIsFailureOpen(true);
  }, []);

  const validateImageFile = useCallback(
    async (file: File) => {
      const mimeOk =
        !!file.type && ALLOWED_IMAGE_MIME_TYPES.includes(file.type);
      const nameOk =
        typeof file.name === 'string' && isAllowedImageFilename(file.name);

      if (!mimeOk && !nameOk) {
        openFailure(unsupportedImageFormatText);
        return false;
      }

      return true;
    },
    [openFailure, unsupportedImageFormatText],
  );

  const handleImageFiles = useCallback(
    async (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);

      if (file.type && !file.type.startsWith('image/')) {
        openFailure(unsupportedImageFormatText);
        return;
      }

      const isValid = await validateImageFile(file);

      if (!isValid) {
        setSelectedFile(null);
        setSelectedImageSize(null);
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        return;
      }

      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setError(null);
      setUploadedAsset(null);
      setSelectedCustomizationSpotType('IMAGE');
      setSelectedFile(file);
      setSelectedImageSize(null);
      setPreviewUrl(URL.createObjectURL(file));
    },
    [
      openFailure,
      previewUrl,
      setSelectedCustomizationSpotType,
      unsupportedImageFormatText,
      validateImageFile,
    ],
  );

  const handleVideoFileSelect = useCallback(
    (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);
      const mimeOk =
        !!file.type && ALLOWED_VIDEO_MIME_TYPES.includes(file.type);
      const nameOk = isAllowedVideoFilename(file.name);
      const validation = parseAndValidateVideoFile(file);

      if ((!mimeOk && !nameOk) || !validation.ok) {
        openFailure(
          validation.ok ? unsupportedVideoFormatText : validation.message,
        );
        return;
      }

      if (selectedVideoLocalUrl) {
        URL.revokeObjectURL(selectedVideoLocalUrl);
      }

      setError(null);
      setSelectedCustomizationSpotType('VIDEO');
      setSelectedVideoFile(file);
      setSelectedVideoLocalUrl(URL.createObjectURL(file));
    },
    [
      openFailure,
      selectedVideoLocalUrl,
      setSelectedCustomizationSpotType,
      unsupportedVideoFormatText,
    ],
  );

  const handlePreviewImageFileSelect = useCallback(
    async (files?: FileList | null) => {
      if (!files?.length) {
        return;
      }

      const [file] = Array.from(files);
      const isValid = await validateImageFile(file);

      if (!isValid) {
        return;
      }

      if (videoPreviewLocalUrl) {
        URL.revokeObjectURL(videoPreviewLocalUrl);
      }

      setVideoPreviewImageFile(file);
      setVideoPreviewLocalUrl(URL.createObjectURL(file));
    },
    [validateImageFile, videoPreviewLocalUrl],
  );

  const handleClearSelectedVideo = useCallback(() => {
    setSelectedVideoFile(null);

    if (selectedVideoLocalUrl) {
      URL.revokeObjectURL(selectedVideoLocalUrl);
    }

    setSelectedVideoLocalUrl(null);
  }, [selectedVideoLocalUrl]);

  const getSpotSnapshot = useCallback((spotMatch: any) => {
    const existingBrandingSpotId: string | null =
      (spotMatch?.brandingSpotId as string | undefined) ||
      (spotMatch?.id as string | undefined) ||
      null;
    const existingRoomSpotId: string | null =
      (spotMatch?.roomSpotId as string | undefined) ||
      (spotMatch?.id as string | undefined) ||
      null;
    const previousData = (spotMatch as any)?.data || null;
    const previousPayload: {
      location?: string;
      url?: string;
      md5?: string;
    } | null =
      previousData && typeof previousData === 'object'
        ? {
            ...(typeof previousData.location === 'string'
              ? { location: previousData.location }
              : {}),
            ...(typeof previousData.url === 'string'
              ? { url: previousData.url }
              : {}),
            ...(typeof previousData.md5 === 'string'
              ? { md5: previousData.md5 }
              : {}),
          }
        : null;
    const previousUrl: string | null =
      (typeof previousData?.url === 'string' && previousData.url.length
        ? previousData.url
        : null) ||
      (typeof previousData?.location === 'string' &&
      previousData.location.length
        ? previousData.location
        : null);

    previousSpotSnapshotRef.current = {
      existed: !!spotMatch,
      spotId: existingBrandingSpotId || existingRoomSpotId,
      dataPayload: previousPayload,
      previousUrl,
    };

    return { existingBrandingSpotId, existingRoomSpotId };
  }, []);

  const writeSpotToApi = useCallback(
    async (
      dataPayload: SpotDataPayload,
      spotType: string,
      spotMatch: any,
      existingBrandingSpotId: string | null,
      existingRoomSpotId: string | null,
    ) => {
      const useBrandingSpots = currentBrandingId && isBrandingModeEnabled;
      const ueIdentifier = selectedCustomizationUeId ?? undefined;

      if (useBrandingSpots && currentBrandingId) {
        let updatedSpot: any = null;

        try {
          if (spotMatch && existingBrandingSpotId) {
            updatedSpot = await BrandingsService.patchBrandingSpot(
              currentBrandingId,
              existingBrandingSpotId,
              buildBrandingSpotMutationPayload(dataPayload, spotType),
            );
          } else {
            updatedSpot = await BrandingsService.createBrandingSpot(
              currentBrandingId,
              {
                name: ueIdentifier,
                ueIdentifier,
                roomSpotId: existingRoomSpotId ?? undefined,
                roomId: currentRoom?.roomId,
                ...buildBrandingSpotMutationPayload(dataPayload, spotType, {
                  ueName: ueIdentifier,
                  mapId: currentRoom?.map?.mapId,
                  updatedBy: currentParticipant
                    ? {
                        participantId: (currentParticipant as any)
                          ?.participantId,
                        name: (currentParticipant as any)?.name,
                        email: (currentParticipant as any)?.email,
                      }
                    : undefined,
                }),
              },
            );
          }
        } catch (patchError: any) {
          if (patchError?.response?.status !== 404) {
            throw patchError;
          }

          updatedSpot = await BrandingsService.createBrandingSpot(
            currentBrandingId,
            {
              name: ueIdentifier,
              ueIdentifier,
              roomId: currentRoom?.roomId,
              ...buildBrandingSpotMutationPayload(dataPayload, spotType, {
                ueName: ueIdentifier,
                mapId: currentRoom?.map?.mapId,
                updatedBy: currentParticipant
                  ? {
                      participantId: (currentParticipant as any)?.participantId,
                      name: (currentParticipant as any)?.name,
                      email: (currentParticipant as any)?.email,
                    }
                  : undefined,
              }),
            },
          );
        }

        const expectedUpdatedSpot = normalizeSpotPreviewImage({
          ...(spotMatch ?? {}),
          ...(updatedSpot ?? {}),
          ...buildBrandingSpotMutationPayload(dataPayload, spotType),
          ueIdentifier:
            (updatedSpot as any)?.ueIdentifier ||
            (spotMatch as any)?.ueIdentifier ||
            selectedCustomizationUeId,
        });

        const refreshed = await BrandingsService.getBrandingSpots(
          currentBrandingId,
          { limit: 200 },
        );
        const normalizedBrandingSpots = (refreshed.spots ?? []).map(
          normalizeSpotPreviewImage,
        );
        const mergedBrandingSpots = mergeSpotIntoList(
          normalizedBrandingSpots,
          expectedUpdatedSpot,
        );
        setBrandingSpots(mergedBrandingSpots);

        if (expectedUpdatedSpot) {
          lastUpdatedSpotIdRef.current =
            (expectedUpdatedSpot as any)?.brandingSpotId ||
            (expectedUpdatedSpot as any)?.id ||
            null;
          sendMessageToUnreal({
            action: 'SPOTS_UPDATED',
            spot: {
              ...expectedUpdatedSpot,
              ueIdentifier:
                (expectedUpdatedSpot as any).ueIdentifier ||
                selectedCustomizationUeId,
            },
          });
        }
      } else if (currentRoom) {
        const patchRoomSpot = async () => {
          if (!spotMatch || !existingRoomSpotId) {
            return false;
          }

          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${existingRoomSpotId}`,
            { data: dataPayload, isActive: true, type: spotType },
          );

          return true;
        };

        let updatedViaPatch = false;

        try {
          updatedViaPatch = await patchRoomSpot();
        } catch (patchError: any) {
          if (patchError?.response?.status !== 404) {
            throw patchError;
          }
        }

        let created: any = null;

        if (!updatedViaPatch) {
          const createRes = await axios.post(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots`,
            {
              name: ueIdentifier,
              type: spotType,
              ueIdentifier,
              isActive: true,
              data: dataPayload,
            },
          );
          created =
            (createRes as any)?.data?.roomSpot || (createRes as any)?.data;
          const createdId: string | null =
            created?.roomSpotId || created?.id || null;

          if (createdId) {
            lastUpdatedSpotIdRef.current = createdId;
          }
        }

        const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
        const normalizedRoomSpots = refreshed.map(normalizeSpotPreviewImage);
        const expectedUpdatedSpot = normalizeSpotPreviewImage({
          ...(spotMatch ?? {}),
          ...(created ?? {}),
          type: spotType,
          isActive: true,
          data: dataPayload,
          ueIdentifier:
            created?.ueIdentifier ||
            (spotMatch as any)?.ueIdentifier ||
            selectedCustomizationUeId,
        });
        const mergedRoomSpots = mergeSpotIntoList(
          normalizedRoomSpots,
          expectedUpdatedSpot,
        );
        setRoomSpots(mergedRoomSpots);

        const updatedSpot =
          findSpotByUeIdentifier(mergedRoomSpots, selectedCustomizationUeId) ||
          null;

        if (updatedSpot) {
          lastUpdatedSpotIdRef.current =
            (updatedSpot as any)?.roomSpotId ||
            (updatedSpot as any)?.id ||
            null;
          sendMessageToUnreal({
            action: 'SPOTS_UPDATED',
            spot: updatedSpot,
          });
        }
      }
    },
    [
      currentBrandingId,
      currentParticipant,
      currentRoom,
      isBrandingModeEnabled,
      selectedCustomizationUeId,
      sendMessageToUnreal,
      setRoomSpots,
      setBrandingSpots,
    ],
  );

  const handleConfirmImage = useCallback(async () => {
    if (!selectedFile) {
      return;
    }

    if (!currentRoom) {
      setError(t('customSpotsEditor.errorNoRoom', 'Room is not available.'));
      return;
    }

    if (!selectedCustomizationUeId) {
      setError(
        t('customSpotsEditor.errorNoSelection', 'No spot selected for update.'),
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setIsApplyingToSurface(false);
    setError(null);

    try {
      const [uploadInfo] = await FilesService.uploadFilesWithProgress(
        [selectedFile],
        setUploadProgress,
      );
      const dataPayload: SpotDataPayload = {
        location: uploadInfo.location,
        url: uploadInfo.url,
      };

      if ((uploadInfo as any).md5) {
        dataPayload.md5 = (uploadInfo as any).md5;
      }

      const spotMatch = findSpotByUeIdentifier(
        spotsSource,
        selectedCustomizationUeId,
      );
      const { existingBrandingSpotId, existingRoomSpotId } =
        getSpotSnapshot(spotMatch);

      await writeSpotToApi(
        dataPayload,
        'IMAGE',
        spotMatch,
        existingBrandingSpotId,
        existingRoomSpotId,
      );
      clearSelectedImageDraft();

      setIsApplyingToSurface(true);
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 2500);
      });
      setIsApplyingToSurface(false);
      setUploadedAsset(uploadInfo);
      if (!suppressAssetUpdatedModal) {
        setIsAssetUpdatedOpen(true);
      }
      setShowCustomSpotsEditor(false);
    } catch (uploadError) {
      console.error(
        `Failed to upload image spot: ${(uploadError as Error)?.message}`,
        { ueId: selectedCustomizationUeId },
      );
      setError(
        t(
          'customSpotsEditor.errorUpload',
          'Failed to upload file, please try again.',
        ),
      );
    } finally {
      setIsUploading(false);
      setIsApplyingToSurface(false);
    }
  }, [
    currentRoom,
    clearSelectedImageDraft,
    getSpotSnapshot,
    selectedCustomizationUeId,
    selectedFile,
    spotsSource,
    setShowCustomSpotsEditor,
    suppressAssetUpdatedModal,
    t,
    writeSpotToApi,
  ]);

  const handleConfirmVideo = useCallback(async () => {
    if (!currentRoom) {
      setError(t('customSpotsEditor.errorNoRoom', 'Room is not available.'));
      return;
    }

    if (!selectedCustomizationUeId) {
      setError(
        t('customSpotsEditor.errorNoSelection', 'No spot selected for update.'),
      );
      return;
    }

    if (!videoPreviewImageFile) {
      setError(
        t(
          'customSpotsEditor.errorNoPreviewImage',
          'Please select a preview image.',
        ),
      );
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStepMessage(
      t('customSpotsEditor.videoStepValidating', 'Validating video...'),
    );
    setIsApplyingToSurface(false);
    setError(null);

    try {
      if (!selectedVideoFile) {
        setError(
          t('customSpotsEditor.errorNoVideo', 'Please select a video file.'),
        );
        return;
      }

      let webmVideoFile: File;

      try {
        setUploadStepMessage(
          t(
            'customSpotsEditor.videoStepConverting',
            'Converting video to WebM...',
          ),
        );
        webmVideoFile = await convertVideoFileToWebm(selectedVideoFile, (p) =>
          setUploadProgress(Math.round(p * 0.45)),
        );
      } catch (conversionError) {
        console.error(
          `Video conversion failed: ${(conversionError as Error)?.message}`,
          { ueId: selectedCustomizationUeId },
        );
        throw new Error(
          t(
            'customSpotsEditor.errorVideoConversion',
            'Video conversion failed. Please try another MP4/MOV/WebM file.',
          ),
        );
      }

      let videoUpload: IUploadFileUrl;

      try {
        setUploadStepMessage(
          t('customSpotsEditor.videoStepUploadingVideo', 'Uploading video...'),
        );
        [videoUpload] = await FilesService.uploadFilesWithProgress(
          [webmVideoFile],
          (p) => setUploadProgress(Math.round(45 + p * 0.3)),
        );
      } catch (videoUploadError) {
        console.error(
          `Video upload failed: ${(videoUploadError as Error)?.message}`,
          { ueId: selectedCustomizationUeId },
        );
        throw new Error(
          t(
            'customSpotsEditor.errorVideoUpload',
            'Video upload failed. Please check the file and try again.',
          ),
        );
      }

      let previewUpload: IUploadFileUrl;

      try {
        setUploadStepMessage(
          t(
            'customSpotsEditor.videoStepUploadingPreview',
            'Uploading preview image...',
          ),
        );
        [previewUpload] = await FilesService.uploadFilesWithProgress(
          [videoPreviewImageFile],
          (p) => setUploadProgress(Math.round(75 + p * 0.2)),
        );
      } catch (previewUploadError) {
        console.error(
          `Preview image upload failed: ${
            (previewUploadError as Error)?.message
          }`,
          { ueId: selectedCustomizationUeId },
        );
        throw new Error(
          t(
            'customSpotsEditor.errorPreviewImageUpload',
            'Preview image upload failed. Please try another image.',
          ),
        );
      }

      const dataPayload: SpotDataPayload = {
        location: videoUpload.location,
        url: videoUpload.url,
        previewImage: previewUpload.url,
      };
      const spotMatch = findSpotByUeIdentifier(
        spotsSource,
        selectedCustomizationUeId,
      );
      const { existingBrandingSpotId, existingRoomSpotId } =
        getSpotSnapshot(spotMatch);

      try {
        setUploadStepMessage(
          t('customSpotsEditor.videoStepSavingSpot', 'Saving spot update...'),
        );
        setUploadProgress(95);
        await writeSpotToApi(
          dataPayload,
          'VIDEO',
          spotMatch,
          existingBrandingSpotId,
          existingRoomSpotId,
        );
        clearSelectedVideoDraft();
      } catch (saveSpotError) {
        console.error(
          `Failed to save video spot: ${(saveSpotError as Error)?.message}`,
          { ueId: selectedCustomizationUeId },
        );
        throw new Error(
          t(
            'customSpotsEditor.errorVideoSpotSave',
            'Failed to save video spot. Please try again.',
          ),
        );
      }

      setUploadProgress(100);
      setIsApplyingToSurface(true);
      await new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 2500);
      });
      setIsApplyingToSurface(false);
      if (!suppressAssetUpdatedModal) {
        setIsAssetUpdatedOpen(true);
      }
      setShowCustomSpotsEditor(false);
    } catch (videoError) {
      console.error(
        `Failed to save video spot: ${(videoError as Error)?.message}`,
        { ueId: selectedCustomizationUeId },
      );
      setError(
        (videoError as Error)?.message ||
          t(
            'customSpotsEditor.errorUpload',
            'Failed to upload file, please try again.',
          ),
      );
    } finally {
      setIsUploading(false);
      setUploadStepMessage(null);
      setIsApplyingToSurface(false);
    }
  }, [
    currentRoom,
    clearSelectedVideoDraft,
    getSpotSnapshot,
    selectedCustomizationUeId,
    selectedVideoFile,
    setShowCustomSpotsEditor,
    spotsSource,
    suppressAssetUpdatedModal,
    t,
    videoPreviewImageFile,
    writeSpotToApi,
  ]);

  const handleClose = useCallback(() => {
    resetState();
    setSelectedCustomizationUeId(null);
    setSelectedCustomizationTargetSize(null);
    setSelectedCustomizationSpotType(null);
    setShowCustomSpotsEditor(false);
    onClose();
  }, [
    onClose,
    resetState,
    setSelectedCustomizationSpotType,
    setSelectedCustomizationTargetSize,
    setSelectedCustomizationUeId,
    setShowCustomSpotsEditor,
  ]);

  const handleSaveAssetUpdated = useCallback(() => {
    setIsAssetUpdatedOpen(false);
    handleClose();
  }, [handleClose]);

  const handleRevertAssetUpdated = useCallback(async () => {
    if (!currentRoom || !selectedCustomizationUeId) {
      handleSaveAssetUpdated();
      return;
    }

    setIsReverting(true);

    try {
      const snapshot = previousSpotSnapshotRef.current;
      const useBrandingSpots = currentBrandingId && isBrandingModeEnabled;

      if (!snapshot?.existed) {
        const spotIdToDelete =
          lastUpdatedSpotIdRef.current ||
          (
            findSpotByUeIdentifier(
              spotsSource,
              selectedCustomizationUeId,
            ) as any
          )?.brandingSpotId ||
          (
            findSpotByUeIdentifier(
              spotsSource,
              selectedCustomizationUeId,
            ) as any
          )?.roomSpotId ||
          (
            findSpotByUeIdentifier(
              spotsSource,
              selectedCustomizationUeId,
            ) as any
          )?.id ||
          null;

        if (spotIdToDelete) {
          if (useBrandingSpots && currentBrandingId) {
            await BrandingsService.deleteBrandingSpot(
              currentBrandingId,
              spotIdToDelete,
            ).catch(() => undefined);
          } else {
            await axios
              .delete(
                `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${spotIdToDelete}`,
              )
              .catch(() => undefined);
          }
        }
      } else if (snapshot?.spotId && snapshot.dataPayload) {
        let revertPayload: {
          location?: string;
          url?: string;
          md5?: string;
        } | null = snapshot.dataPayload;

        if (snapshot.previousUrl) {
          try {
            const resp = await fetch(snapshot.previousUrl);
            const blob = await resp.blob();
            const file = new File(
              [blob],
              `spot-${selectedCustomizationUeId}.png`,
              { type: blob.type || 'image/png' },
            );
            const [uploadInfo] = await FilesService.uploadFiles([file]);
            revertPayload = {
              location: uploadInfo.location,
              url: uploadInfo.url,
              ...(((uploadInfo as any).md5 && {
                md5: (uploadInfo as any).md5,
              }) ||
                {}),
            };
          } catch {
            revertPayload = snapshot.dataPayload;
          }
        }

        if (useBrandingSpots && currentBrandingId) {
          await BrandingsService.patchBrandingSpot(
            currentBrandingId,
            snapshot.spotId,
            { isActive: true, data: revertPayload || {} },
          );
        } else {
          await axios.patch(
            `${env.REACT_APP_API_URL}api/v2/rooms/${currentRoom.roomId}/spots/${snapshot.spotId}`,
            {
              isActive: true,
              data: revertPayload || {},
            },
          );
        }
      } else {
        handleSaveAssetUpdated();
        return;
      }

      if (useBrandingSpots && currentBrandingId) {
        const refreshed = await BrandingsService.getBrandingSpots(
          currentBrandingId,
          { limit: 200 },
        );
        const normalizedBrandingSpots = (refreshed.spots ?? []).map(
          normalizeSpotPreviewImage,
        );
        setBrandingSpots(normalizedBrandingSpots);
        const revertedSpot =
          findSpotByUeIdentifier(
            normalizedBrandingSpots,
            selectedCustomizationUeId,
          ) || null;

        if (revertedSpot) {
          sendMessageToUnreal({
            action: 'SPOTS_UPDATED',
            spot: revertedSpot,
          });
        }
      } else {
        const refreshed = await EventsService.getRoomSpots(currentRoom.roomId);
        const normalizedRoomSpots = refreshed.map(normalizeSpotPreviewImage);
        setRoomSpots(normalizedRoomSpots);
        const revertedSpot =
          findSpotByUeIdentifier(
            normalizedRoomSpots,
            selectedCustomizationUeId,
          ) || null;

        if (revertedSpot) {
          sendMessageToUnreal({
            action: 'SPOTS_UPDATED',
            spot: revertedSpot,
          });
        }
      }
    } catch {
      setError(
        t(
          'customSpotsEditor.revertFailed',
          'Failed to revert, please try again.',
        ),
      );
    } finally {
      setIsReverting(false);
      setIsAssetUpdatedOpen(false);
      handleClose();
    }
  }, [
    currentBrandingId,
    currentRoom,
    handleClose,
    handleSaveAssetUpdated,
    isBrandingModeEnabled,
    spotsSource,
    selectedCustomizationUeId,
    sendMessageToUnreal,
    setRoomSpots,
    setBrandingSpots,
    t,
  ]);

  const handleTryAgain = useCallback(() => {
    setIsFailureOpen(false);
    setFailureDetailsOpen(false);

    if (isVideoSpot) {
      videoFileInputRef.current?.click();
    } else {
      fileInputRef.current?.click();
    }
  }, [isVideoSpot]);

  const handleLoadAnother = useCallback(() => {
    setError(null);
    setUploadedAsset(null);
    setSelectedFile(null);
    setSelectedImageSize(null);

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl(null);
    fileInputRef.current?.click();
  }, [previewUrl]);

  const handleCopySpotId = useCallback(async () => {
    if (!selectedCustomizationUeId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(selectedCustomizationUeId);
    } catch {
      return;
    }
  }, [selectedCustomizationUeId]);

  const saveSpotDistance = useCallback(
    async (distance: number | null) => {
      if (!currentBrandingId || !isBrandingModeEnabled) {
        return;
      }

      const spotMatch = findSpotByUeIdentifier(
        spotsSource,
        selectedCustomizationUeId,
      );
      const brandingSpotId: string | null =
        (spotMatch as any)?.brandingSpotId || (spotMatch as any)?.id || null;

      if (!brandingSpotId) {
        return;
      }

      try {
        await BrandingsService.patchBrandingSpot(
          currentBrandingId,
          brandingSpotId,
          { distance },
        );

        const refreshed = await BrandingsService.getBrandingSpots(
          currentBrandingId,
          { limit: 200 },
        );
        const normalizedBrandingSpots = (refreshed.spots ?? []).map(
          normalizeSpotPreviewImage,
        );
        setBrandingSpots(normalizedBrandingSpots);

        const updatedSpot = findSpotByUeIdentifier(
          normalizedBrandingSpots,
          selectedCustomizationUeId,
        );

        if (updatedSpot) {
          sendMessageToUnreal({
            action: 'SPOTS_UPDATED',
            spot: updatedSpot,
          });
        }
      } catch (distanceError) {
        console.error(
          `Failed to save spot distance: ${
            (distanceError as Error)?.message
          }`,
          { brandingSpotId, brandingId: currentBrandingId },
        );
        throw distanceError;
      }
    },
    [
      currentBrandingId,
      isBrandingModeEnabled,
      selectedCustomizationUeId,
      sendMessageToUnreal,
      setBrandingSpots,
      spotsSource,
    ],
  );

  const dialogTitle = isVideoSpot
    ? t('customSpotsEditor.updateVideoTitle', {
        defaultValue: 'Update Spot Video',
      })
    : selectedFile
    ? t('customSpotsEditor.customizeTitle', { defaultValue: 'Customize Spot' })
    : t('customSpotsEditor.updateTitle', { defaultValue: 'Update Spot Media' });
  const displayedPreviewUrl = previewUrl || existingSpotImageUrl || null;
  const hasPreview = !!displayedPreviewUrl;
  const isBusy = isUploading || isApplyingToSurface || isReverting;
  const isImageReadyToUpload = !!selectedFile && !isBusy;
  const isVideoReadyToSubmit =
    !isBusy && !!videoPreviewImageFile && !!selectedVideoFile;
  const handleConfirm = isVideoSpot ? handleConfirmVideo : handleConfirmImage;

  return {
    t,
    dialogTitle,
    isVideoSpot,
    isDragging,
    setIsDragging,
    isPreviewImageDragging,
    setIsPreviewImageDragging,
    isUploading,
    uploadProgress,
    uploadStepMessage,
    isApplyingToSurface,
    error,
    isFailureOpen,
    setIsFailureOpen,
    failureDetailsOpen,
    setFailureDetailsOpen,
    failureMessage,
    isAssetUpdatedOpen,
    isReverting,
    selectedFile,
    selectedVideoFile,
    selectedVideoLocalUrl,
    videoPreviewLocalUrl,
    selectedCustomizationUeId,
    requirementsText,
    unsupportedImageFormatText,
    targetAspect,
    displayedPreviewUrl,
    hasPreview,
    isBusy,
    isImageReadyToUpload,
    isVideoReadyToSubmit,
    fileInputRef,
    videoFileInputRef,
    previewImageFileInputRef,
    handleImageFiles,
    handleVideoFileSelect,
    handleClearSelectedVideo,
    handlePreviewImageFileSelect,
    handleConfirm,
    handleConfirmImage,
    handleConfirmVideo,
    handleClose,
    handleSaveAssetUpdated,
    handleRevertAssetUpdated,
    handleTryAgain,
    handleLoadAnother,
    handleCopySpotId,
    saveSpotDistance,
    setSelectedImageSize,
  };
};

export type CustomSpotsEditorController = ReturnType<
  typeof useCustomSpotsEditor
>;
